// Bringing a message of the open conversation into view, however far back it
// lies. The conversation renders the room's live timeline, so a message not
// loaded yet is reached by loading older pages until it arrives. That keeps
// the timeline continuous down to the newest message — reactions, redactions,
// reply lookups and incoming messages go on working unchanged. Opening a
// separate timeline around the event would skip the loading at the price of a
// second rendering mode; revealEvent is where it would plug in.

const PAGE_SIZE = 100;
// About 2,000 messages. Loaded history renders its media and link previews,
// so going further is the user's call (status "capped").
const MAX_PAGES = 20;
// Clock skew between senders: only a timeline clearly older than the message
// proves the message is not coming.
const TS_SLACK_MS = 60 * 1000;
// Media above the message that is still loading pushes it down; it is
// re-centred on every load for this long, unless the user takes over.
const PIN_MS = 1500;
const USER_SCROLL_EVENTS = ["wheel", "touchstart", "pointerdown", "keydown"];

let releasePin = null;

export function findMessageElement(timeline, eventId) {
  if (!timeline || !eventId) return null;
  return timeline.querySelector(`.bryght-message[data-event-id="${CSS.escape(eventId)}"]`);
}

// → { status: "found", element, loadedHistory }
//   or { status: "not_found" | "capped" | "aborted" | "error" }
export async function revealEvent(ui, room, eventId, { ts = null, signal = null, maxPages = MAX_PAGES, pageSize = PAGE_SIZE } = {}) {
  const timeline = document.getElementById("bryght-timeline");
  if (!timeline) return { status: "aborted" };

  const rendered = findMessageElement(timeline, eventId);
  if (rendered) return { status: "found", element: rendered, loadedHistory: false };

  const live = room.getLiveTimeline();
  const isLoaded = () => live.getEvents().some((event) => event.getId() === eventId);
  // In the timeline but without an element: nothing the conversation shows.
  if (isLoaded()) return { status: "not_found" };

  const isCurrent = () => !signal?.aborted && ui.currentRoomId === room.roomId && room.getLiveTimeline() === live;

  // Rendered one by one, every older event would rebuild all date dividers —
  // quadratic over a few thousand messages. And the scroll-triggered
  // pagination has to stay out while this one runs.
  ui.suspendBackfillRendering(room.roomId);
  timeline.dataset.paginating = "true";

  let status = "capped";
  try {
    for (let page = 0; page < maxPages; page++) {
      const more = await ui.client.paginateEventTimeline(live, { backwards: true, limit: pageSize });
      if (!isCurrent()) { status = "aborted"; break; }
      if (isLoaded()) { status = "found"; break; }

      const oldest = live.getEvents()[0];
      if (!more || (ts && oldest && oldest.getTs() < ts - TS_SLACK_MS)) { status = "not_found"; break; }
    }
  } catch (error) {
    console.error("[BryghtChat] Loading history up to a message failed:", error);
    status = "error";
  } finally {
    // The conversation may have been closed and reopened meanwhile: whatever
    // timeline shows the room now gets what was loaded, once no other jump
    // is still loading into it.
    if (ui.resumeBackfillRendering(room.roomId) && ui.currentRoomId === room.roomId) {
      const current = document.getElementById("bryght-timeline");
      if (current) {
        current.dataset.paginating = "false";
        ui.renderMissingOlderEvents(room, current);
      }
    }
  }

  if (status !== "found") return { status };
  const element = findMessageElement(document.getElementById("bryght-timeline"), eventId);
  return element ? { status, element, loadedHistory: true } : { status: "not_found" };
}

// Centres the message, expands it if it is a long one and flashes it.
export function focusMessage(timeline, element, { smooth = false } = {}) {
  expandMessage(element);

  // Everything that snaps the timeline to the newest message reads this flag.
  timeline.dataset.atBottom = "false";
  const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  centerIn(timeline, element, smooth && !reduceMotion ? "smooth" : "auto");

  element.classList.remove("bryght-message--highlight");
  void element.offsetWidth; // restarts the animation
  element.classList.add("bryght-message--highlight");

  pinWhileMediaLoads(timeline, element);
}

function expandMessage(element) {
  const collapsed = element.querySelector(".bryght-message-text-content.is-collapsed");
  collapsed?.parentElement.querySelector(".bryght-message-expand-btn")?.click();
}

// The timeline is positioned, so it is the message's offset parent. Scrolling
// it directly leaves the page and the drawer where they are, which
// scrollIntoView would not.
function centerIn(timeline, element, behavior) {
  const top = element.offsetTop - (timeline.clientHeight - element.offsetHeight) / 2;
  timeline.scrollTo({ top: Math.max(0, top), behavior });
}

function pinWhileMediaLoads(timeline, element) {
  releasePin?.();

  const recenter = () => centerIn(timeline, element, "auto");
  const release = () => {
    clearTimeout(timer);
    // Media load events do not bubble; the capturing listener still sees them.
    timeline.removeEventListener("load", recenter, true);
    USER_SCROLL_EVENTS.forEach((type) => timeline.removeEventListener(type, release));
    if (releasePin === release) releasePin = null;
  };
  const timer = setTimeout(release, PIN_MS);

  timeline.addEventListener("load", recenter, true);
  USER_SCROLL_EVENTS.forEach((type) => timeline.addEventListener(type, release, { passive: true }));
  releasePin = release;
}
